import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '@/api/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [limits, setLimits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check auth status on mount
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authApi.getMe();
      if (data) {
        setUser(data.user);
        setWorkspace(data.workspace);
        setSubscription(data.subscription);
        setLimits(data.limits);
        setUsage(data.usage);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login with Emergent OAuth
  const login = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await authApi.login(sessionId);
      setUser(data.user);
      setWorkspace(data.workspace);
      // Refresh full auth state
      await checkAuth();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setWorkspace(null);
      setSubscription(null);
      setLimits(null);
      setUsage(null);
    }
  }, []);

  // Refresh subscription data
  const refreshSubscription = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  // Check if user can perform action based on limits
  const canCreateMachine = useCallback(() => {
    if (!limits || !usage) return true; // Allow if no limits set
    if (limits.machines === -1) return true; // Unlimited
    return usage.machines < limits.machines;
  }, [limits, usage]);

  const canExport = useCallback(() => {
    if (!limits || !usage) return true;
    if (limits.exports === -1) return true;
    return usage.exports < limits.exports;
  }, [limits, usage]);

  const canAddStates = useCallback((currentStates) => {
    if (!limits) return true;
    if (limits.states_per_machine === -1) return true;
    return currentStates < limits.states_per_machine;
  }, [limits]);

  const value = {
    user,
    workspace,
    subscription,
    limits,
    usage,
    loading,
    error,
    isAuthenticated: !!user,
    tier: subscription?.tier || 'free',
    login,
    logout,
    refreshSubscription,
    checkAuth,
    canCreateMachine,
    canExport,
    canAddStates,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
